import { all, takeLatest, put, select /* select */, call } from 'redux-saga/effects';
import { DataItemType, FetchConfigDicts, IAction, INavMenu, IRouteMenuConfig, TargetVO } from 'models/global';
import routerConfig, { IRouterConfig } from 'routes/router.config';
import { DSP_PEAR_DICT_CONFIG, getMenuConfig } from 'constants/global';
import { message } from 'antd';
import { postRequest } from 'utils/request';
import { ADVERTISER_DETAIL_URL, CONFIG_DICTS } from 'constants/serviceUrl';
import { takeEvery } from 'redux-saga';
import { getPearDictConfig } from 'service/pear';
import { customLocalStorage } from 'utils/storage';
import { safeStringify } from 'utils/string';
import { getAuthority } from './selector';
import {
  GLOBAL_INIT,
  INIT_NAV_MENU,
  UPDATE_MENU,
  UPDATE_USER_INFO,
  GET_CONFIG_DICTS,
  UPDATE_CONFIG_DICTS,
  UPDATE_AD_ACCOUNT_INFO,
  GET_AD_ACCOUNT_INFO,
  USR_LOGIN_SUCCESS,
  UPDATE_CURRENCY,
  UPDATE_PEAR_CONFIG_DICTS,
  GET_PEAR_CONFIG_DICTS,
} from './action';

export default function* globalWatcher() {
  yield all([
    takeLatest(GLOBAL_INIT, globalInit),
    takeLatest(INIT_NAV_MENU, navMenuInit),
    takeEvery(GET_CONFIG_DICTS, fetchConfigDicts),
    takeEvery(GET_PEAR_CONFIG_DICTS, fetchPearConfigDicts),
    takeLatest(GET_AD_ACCOUNT_INFO, fetchAdAccountInfo),
  ]);
}

function* globalInit({ payload: data }: IAction) {
  yield put({
    type: UPDATE_USER_INFO,
    payload: data,
  });
  yield put({
    type: USR_LOGIN_SUCCESS,
  });
}

// 匹配菜单和路由
export function menuMatchRouteConfig(oneMenu: IRouteMenuConfig, config: IRouterConfig[]): INavMenu[] {
  const matchResult: IRouterConfig | undefined = config.find((configRoute: IRouterConfig) => {
    if (configRoute.path === '*') {
      return false;
    }
    const matchPath = configRoute.path.replace(/\//g, '\\/').replace(/:\w+/g, '\\w+');
    const matchReg = new RegExp(`^${matchPath}$`, 'g');
    return matchReg.test(oneMenu.path);
  });

  if (matchResult) {
    return [
      {
        path: oneMenu.path,
        icon: oneMenu.icon,
        title: oneMenu.title,
        authority: matchResult.authority,
        externalPath: oneMenu.externalPath,
        children:
          matchResult.routes &&
          oneMenu.children &&
          oneMenu.children
            .map((childrenMenu: any) => {
              return menuMatchRouteConfig(childrenMenu, matchResult.routes!);
            })
            .reduce((preRoute: INavMenu[], nextRoute: INavMenu[]) => preRoute.concat(nextRoute), []),
      },
    ];
  }
  return config
    .filter((configRoute: IRouterConfig) => configRoute.routes && configRoute.routes.length)
    .map((configRoute: IRouterConfig) => {
      return menuMatchRouteConfig(oneMenu, configRoute.routes!);
    })
    .reduce((preRoute: INavMenu[], nextRoute: INavMenu[]) => preRoute.concat(nextRoute), []);
}

// 生成导航菜单
export function generateNavMenu(menu: IRouteMenuConfig[], config: IRouterConfig[]): INavMenu[] {
  const navMenu: INavMenu[] = menu
    .map((oneMenu: IRouteMenuConfig) => {
      return menuMatchRouteConfig(oneMenu, config);
    })
    .reduce((preMenu: INavMenu[], nextMenu: INavMenu[]) => preMenu.concat(nextMenu), []);
  return navMenu;
}

// 过滤无权限菜单
function authNavMenu(menu: INavMenu[], authority: number[]): INavMenu[] {
  return menu.reduce((filterMenu: INavMenu[], oneMenu: INavMenu) => {
    // sup层不配置权限代表任何人都能访问
    if (!oneMenu.authority || !oneMenu.authority.length) {
      // 判断有无sub层级
      if (oneMenu.children) {
        const childrenAuth = authNavMenu(oneMenu.children!, authority);

        // 判断过滤后的sub层级
        if (childrenAuth.length) {
          return filterMenu.concat({
            ...oneMenu,
            children: childrenAuth,
          });
        }
        return filterMenu;
      }
      return filterMenu.concat(oneMenu);
    }

    const permission = oneMenu.authority.every((item) => authority.includes(item));

    // 判断是否有权限
    if (permission) {
      if (oneMenu.children) {
        const childrenAuth = authNavMenu(oneMenu.children!, authority);

        // 判断过滤后的sub层级
        if (childrenAuth.length) {
          return filterMenu.concat({
            ...oneMenu,
            children: childrenAuth,
          });
        }
        return filterMenu;
      }
      return filterMenu.concat(oneMenu);
    }
    return filterMenu;
  }, []);
}

// 初始化导航菜单
function* navMenuInit({ payload }: IAction) {
  const authority = yield select(getAuthority);
  const menuConfig = getMenuConfig();
  // 根据路由生成菜单
  const menu: INavMenu[] = generateNavMenu(menuConfig, routerConfig);

  // 过滤无权限菜单
  const authMenu: INavMenu[] = authNavMenu(menu, authority);

  yield put({
    type: UPDATE_MENU,
    payload: authMenu,
  });
}

// 获取数据字典
function* fetchConfigDicts() {
  try {
    const data: FetchConfigDicts = yield postRequest(CONFIG_DICTS);

    if (!data) {
      return;
    }

    yield put({
      type: UPDATE_CONFIG_DICTS,
      payload: {
        ...data,
        language: data.language.map((language: any) => ({ label: language.name, value: language.id })),
        location: initTreeData(data?.digLocation?.length ? data?.digLocation : data?.location),
        oldLocation: initTreeData(data?.location),
        interest: initTreeData(data.interest),
        media_app: data.media_app
          .map((app) => ({ label: app.name, value: app.pkgName }))
          .filter((app) => data.media_rules[app.value]),
        looklike_media_app: data.looklike_media_app.map((app) => ({ label: app.name, value: app.pkgName })),
        age: data.age.map((app) => ({ label: app.name, value: app.id })),
        android_versions: data.android_versions.map((version) => ({ label: version.name, value: version.id })),
        ios_versions: data.ios_versions.map((version) => ({ label: version.name, value: version.id })),
        max_android_versions: data.max_android_versions.map((version) => ({ label: version.name, value: version.id })),
        max_ios_versions: data.max_ios_versions.map((version) => ({ label: version.name, value: version.id })),
        operation: data.operation.map((operation) => ({
          label: operation.name,
          value: operation.id,
          country: operation.country,
          rate: operation?.rate,
        })),
        gender: data.gender.map((app) => ({ label: app.name, value: app.id })),
        country: data.country.map((app) => ({ label: app.name, value: app.id })),
        lowest_ios_model_versions: data?.lowest_ios_model_versions?.map?.((el) => ({
          label: el?.name,
          value: el?.code,
        })),
      },
    });
  } catch (e) {
    message.error(e.message);
  }
}

function* fetchPearConfigDicts() {
  try {
    const data = yield call(getPearDictConfig);
    if (!data) {
      return;
    }
    // 将sentry配置设置到本地
    customLocalStorage.setItem(DSP_PEAR_DICT_CONFIG, safeStringify(data || []));
    yield put({
      type: UPDATE_PEAR_CONFIG_DICTS,
      payload: data,
    });
  } catch (e) {
    message.error(e.message);
  }
}

// 获取当前广告账户信息
function* fetchAdAccountInfo() {
  try {
    const data = yield postRequest(ADVERTISER_DETAIL_URL);
    yield put({
      type: UPDATE_AD_ACCOUNT_INFO,
      payload: data.adAccount,
    });
    yield put({
      type: UPDATE_CURRENCY,
      payload: data.adAccount.currency,
    });
  } catch (e) {
    message.error(e.message);
  }
}

function initTreeData(tree: TargetVO[]): DataItemType[] {
  return tree.map((node: TargetVO) => {
    const res: DataItemType = {
      label: node.name,
      value: node.id,
      code: node.parentId || node.id,
      children: node.children ? initTreeData(node.children) : undefined,
    };
    return res;
  });
}
